import React, { useState, useEffect, createRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { omit } from 'lodash'

import Card from 'components/Shared/Card'
import StackedAreaChartComp from 'components/Shared/Charts/StackedAreaChart'
import { activityOverTime as activityOverTimeType } from 'types/ActivityOverTime'
import CardSkeleton from 'components/Shared/SkeletonStack/Card/CardSkeleton'
import NoDataComp from 'components/Shared/NoData'
import SelectDropdown from 'components/Shared/SelectDropdown'
import { SelectDropdown as SelectDropdownType } from 'types/SelectDropdown'
import { useAppSelector } from 'redux/Hook'
import { sortedActivityKeys } from 'components/Reporting/Overview/GraphicalDetails/ActivityOverTime/Constant'

type ActivityOverTimeCardProps = {
  activityOverTimeData: activityOverTimeType
  isLoading: boolean
  onSetActivityFilterDropdown: (interval: string) => void
  activityOverTimeOptions: SelectDropdownType
  activityOverTimeSelection: { period: string }
}

interface ActivityOverTimeKeyType {
  key: string
}

const ActivityOverTime = ({
  activityOverTimeData,
  isLoading,
  onSetActivityFilterDropdown,
  activityOverTimeOptions,
  activityOverTimeSelection,
}: ActivityOverTimeCardProps) => {
  const { t } = useTranslation()

  const [legendKeys, setLegendKeys] = useState<ActivityOverTimeKeyType[]>([])
  const { data = [], info = '' } = activityOverTimeData || {}
  const {
    stakeholderDashboardDetails: { accessToken = '' },
  }: any = useAppSelector((state) => state)

  useEffect(() => {
    if (data.length) {
      //removing 'metric' key as it is not required in keys for chart legends
      const keyData = omit(data[0], 'metric')

      const keys = Object.keys(keyData).map((str) => ({ key: str }))

      setLegendKeys(sortedActivityKeys(keys))
    }
  }, [data])

  // create activity over time ref
  const createMoreVertRef = createRef<HTMLDivElement>()

  const moreVertProps = {
    moreVertOptions: ['jpeg', 'png', 'pdf', 'csv'],
    moreVertRef: createMoreVertRef,
    fileNameKey: 'activityOverTime',
  }

  const buildSkeleton = (
    <CardSkeleton showHeaderSkeleton={false} sectionCount={1} height={300} />
  )

  const getDataComp = useCallback(() => {
    const trackActionDetails = {
      eventName: 'Chart Legend Toggled',
      featureName: 'Overview Dashboard',
      sectionName: 'Widget',
      widgetName: 'Activity over time',
      widgetType: 'area chart',
    }
    if (data?.length) {
      return (
        Object.keys(legendKeys).length && (
          <StackedAreaChartComp
            keys={legendKeys}
            xAxisDataKey="metric"
            data={data}
            title={`${t('reporting.overview.activityOverTime')}`}
            trackActionDetails={trackActionDetails}
          />
        )
      )
    }
    return <NoDataComp />
  }, [legendKeys, data])

  const getActivityFilterDropdown = () => {
    return (
      onSetActivityFilterDropdown && (
        <SelectDropdown
          value={activityOverTimeSelection?.period}
          selectDropdownOptions={activityOverTimeOptions}
          onChangeFilterDropdown={onSetActivityFilterDropdown}
        />
      )
    )
  }

  const activityChartContent = () => {
    return isLoading ? buildSkeleton : getDataComp()
  }

  return (
    <Card
      topContentSpacing="none"
      moreVertProps={moreVertProps}
      content={activityChartContent()}
      info={info}
      showMoreAction={!!data?.length && !accessToken}
      subHeaderContent={getActivityFilterDropdown()}
      header={t('reporting.overview.activityOverTime')}
    />
  )
}

export default ActivityOverTime
